import { RecipePreAPIHookContext } from 'supertokens-auth-react/lib/build/recipe/recipeModule/types';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import Multitenancy from 'supertokens-auth-react/recipe/multitenancy';
import Session from 'supertokens-auth-react/recipe/session';
import ThirdParty from 'supertokens-auth-react/recipe/thirdparty';

import { useRouter } from '@/i18n/routing';
import { fetchClientDetails } from '@/lib/api';
import { CountrySelector } from '@/lib/context/CountrySelector';
import { SalutationSelector } from '@/lib/context/SalutationSelector';
import { TermsAgreement } from '@/lib/context/SignupFields';
import { logger } from '@/lib/logger';
import { envParameters } from '@/lib/utils/constants';
import { getFeatureFlagsHeaderSupertokensValue } from '@/lib/utils/featureFlags';
import { getCookie } from '@/lib/utils/getCookies';

import { tenantMap } from './constants';
import { fetchAndStoreAccountDetails, fetchStoreAndIdentify } from './handlers';
import { superTokensStyles } from './styles';
import { getTranslation } from './translations';

import type {
  AnalyticsIdentifyEvent,
  AnalyticsTrackEvent,
} from '@/lib/contexts/analytics';
import type { TranslationStore } from 'supertokens-auth-react/lib/build/translation/translationHelpers';
import type { SuperTokensConfig } from 'supertokens-auth-react/lib/build/types';

import type { SiteConfigResponse } from '@/lib/api/types';

export * from './types';

interface InitAuthOptions {
  track?: (event: AnalyticsTrackEvent) => void;
  identify?: (event: AnalyticsIdentifyEvent) => void;
  reset?: () => void;
}

const superTokensRouterInstance: {
  router?: ReturnType<typeof useRouter>;
  pathName?: string;
} = {};

export function setRouter(
  router: ReturnType<typeof useRouter>,
  pathName: string,
) {
  superTokensRouterInstance.router = router;
  superTokensRouterInstance.pathName = pathName;
}

export const initAuth = async (
  config: SiteConfigResponse,
  translations: TranslationStore,
  options?: InitAuthOptions,
): Promise<SuperTokensConfig> => {
  const track = options?.track;
  const identify = options?.identify;
  const reset = options?.reset;
  const apiDomain = envParameters.superTokensApiUrl;
  const websiteDomain = window.location.origin;
  const supportedLocales = new Set(
    config.languages.map(language => language.shortcode.toLowerCase()),
  );

  const getCurrentLocale = () => {
    const pathLocale = window.location.pathname
      .split('/')
      .filter(Boolean)[0]
      ?.toLowerCase();

    if (pathLocale && supportedLocales.has(pathLocale)) {
      return pathLocale;
    }

    const cookieLocale = (
      getCookie('NEXT_LOCALE') ||
      getCookie('language') ||
      ''
    ).toLowerCase();

    if (cookieLocale && supportedLocales.has(cookieLocale)) {
      return cookieLocale;
    }

    return config.default_language.shortcode;
  };

  const lang = getCurrentLocale();

  let customEnglishTranslations = translations.en;
  let customGermanTranslations = translations.de;

  if (config.configuration.tenant_id === tenantMap.BSO) {
    customEnglishTranslations = {
      ...customEnglishTranslations,
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_START: `Don't have an account with us?`,
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_SIGN_UP_LINK: 'Register now',
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_UP_SIGN_IN_LINK: 'Login',
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_UP_START:
        'Already have an account at Bayerische Staatsoper?',
      AUTH_PAGE_HEADER_TITLE_SIGN_IN: 'Login',
      AUTH_PAGE_HOUSE_NUMBER_VALID_TEXT: 'House number should be a valid text.',
      AUTH_PAGE_FIRST_NAME_VALID_TEXT: 'Please enter a valid first name.',
      AUTH_PAGE_SURNAME_VALID_TEXT: 'Please enter a valid surname.',
      AUTH_PAGE_STREET_VALID_TEXT: 'Please enter a valid street name.',
      AUTH_PAGE_POSTCODE_VALID_TEXT: 'Please enter a valid postcode.',
      AUTH_PAGE_CITY_VALID_TEXT: 'Please enter a valid city.',
      AUTH_SIGNUP_BSO_PASSWORD_REQUIREMENTS:
        'Password must be at least 8 characters long, contain at least 1 letter, 1 digit, and no spaces.',
    };
    customGermanTranslations = {
      ...customGermanTranslations,
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_START: `Noch kein Konto bei uns?`,
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_IN_SIGN_UP_LINK: 'Jetzt registrieren',
      AUTH_PAGE_HEADER_SUBTITLE_SIGN_UP_START: `Sie haben bereits ein Konto bei der Bayerischen Staatsoper?`,
      AUTH_CREATE_ACCOUNT: 'Ein Konto erstellen',
      AUTH_PAGE_HOUSE_NUMBER_VALID_TEXT:
        'Bitte geben Sie eine gültige Hausnummer ein.',
      AUTH_PAGE_FIRST_NAME_VALID_TEXT:
        'Bitte geben Sie einen gültigen Vornamen ein.',
      AUTH_PAGE_SURNAME_VALID_TEXT:
        'Bitte geben Sie einen gültigen Nachnamen ein.',
      AUTH_PAGE_STREET_VALID_TEXT: 'Bitte geben Sie eine gültige Straße ein.',
      AUTH_PAGE_POSTCODE_VALID_TEXT:
        'Bitte geben Sie eine gültige Postleitzahl ein.',
      AUTH_PAGE_CITY_VALID_TEXT: 'Bitte geben Sie eine gültige Stadt ein.',
      AUTH_SIGNUP_BSO_PASSWORD_REQUIREMENTS:
        'Das Passwort muss mindestens 8 Zeichen lang sein, mindestens 1 Buchstaben, 1 Ziffer enthalten und keine Leerzeichen haben.',
      'The password reset link has expired or is invalid. Please request a new one.':
        'Der Link zum Zurücksetzen des Passworts ist abgelaufen oder ungültig. Bitte fordern Sie einen neuen an.',
    };
  }

  const customPreAPIHook = async (
    context: RecipePreAPIHookContext<string>,
  ): Promise<{
    requestInit: RequestInit;
    url: string;
  }> => {
    const currentLang = getCurrentLocale();

    let modifiedBody = context.requestInit.body;
    if (typeof modifiedBody === 'string') {
      try {
        const bodyJson = JSON.parse(modifiedBody);
        if (bodyJson.formFields) {
          bodyJson.formFields = bodyJson.formFields.filter(
            (field: { id: string; value: string }) =>
              field.id !== 'newsletterStatus.1' &&
              !(
                field.id === 'customer.countryKey' && field.value === 'error'
              ) &&
              !(
                field.id === 'customer.salutationKey' && field.value === 'error'
              ),
          );
          modifiedBody = JSON.stringify(bodyJson);
        }
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        logger.error(typedError.message, {
          source: 'nextjs-app-lib/auth/index',
          digest: typedError.digest,
          stack: typedError.stack,
        });
      }
    }

    const featureFlags = getFeatureFlagsHeaderSupertokensValue(
      config.configuration.tenant_id,
    );

    return {
      requestInit: {
        ...context.requestInit,
        headers: {
          ...context.requestInit.headers,
          'Accept-Language': currentLang,
          ...(featureFlags && { 'X-Feature-Flags': featureFlags }),
        },
        body: modifiedBody,
      },
      url: context.url,
    };
  };

  const handlerDeps = {
    fetchClientDetails,
    siteId: config.id,
    tenantId: config.configuration.tenant_id,
    lang,
    identify,
    track,
  };

  const getDefaultSalutation = () => {
    const langCode =
      getCookie('NEXT_LOCALE') ||
      getCookie('language') ||
      config.default_language.shortcode;
    const salutationMap: Record<string, string> = {
      de: '1',
      en: '15',
    };
    return salutationMap[langCode] || '1';
  };

  const validateTextField = (
    value: string,
    errorKey: string,
  ): string | undefined => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return errorKey;
    }
    const hasLetterOrDigit = /[\p{L}\p{N}]/u.test(trimmed);
    if (!hasLetterOrDigit) {
      return errorKey;
    }
    if (errorKey.includes('HOUSE_NUMBER')) {
      const isValid = /^[\p{L}\p{N}\s\-\/\\.,#]+$/u.test(trimmed);
      if (!isValid) {
        return errorKey;
      }
    } else {
      const isValid = /^[\p{L}\p{N}\s\-'.]+$/u.test(trimmed);
      if (!isValid) {
        return errorKey;
      }
    }
    return undefined;
  };

  return {
    appInfo: {
      appName: 'Vialma App',
      apiDomain: apiDomain as string,
      websiteDomain,
    },
    languageTranslations: {
      translations: {
        ...translations,
        en: customEnglishTranslations,
        de: customGermanTranslations,
      },
    },
    usesDynamicLoginMethods: true,
    useShadowDom: false,
    style: `
  ${superTokensStyles}
  ${
    config.name === tenantMap.staatsoperTV &&
    `
  [data-supertokens~=link] {
  font-size: var(--text-body-sm);
  font-weight: var(--font-weight-h6);
  color: var(--color-text-primary);
  font-family: var(--font-body) !important;
  text-decoration: underline;
  }
  [data-supertokens~=inputWrapper ] {
   border-radius: unset;
}
[data-supertokens~=input] {
  border-radius: unset;
  height: 100%;
  width: 100%
}
[data-supertokens~=button] {
  border-radius: unset !important;
}
[data-supertokens~=dropDownTrigger] {
  background-color: var(--drop-down-background-default) !important;
  color: var(--drop-down-heading-text-color-default);
  border-radius: var(--drop-down-item-radius) !important;
}
 `
  }
  ${
    config.name === tenantMap.juliaFisher
      ? `
    [data-supertokens~=headerTitle] {
      display: none !important;
     }
    [data-supertokens~="divider"] {
     display: block !important;
     margin: 0 32px 28px 28px;
    }
    [data-supertokens~="withBackButton"] {
      display: none !important;
    }
    [data-supertokens~=providerContainer] {
       margin-right: 33px;
       margin-left: 30px;
      [data-supertokens~=button] {
        background-color: ${config.theme.colour.cta_buttons_bg_clr};
        color: ${config.theme.colour.cta_buttons_clr};
        border-radius: var(--radius-md) !important;
        [data-supertokens~="providerButtonText"] {
        font-size: var(--text-body-sm);
      }
      }
      [data-supertokens~=button]:hover {
        background-color: ${config.theme.colour.cta_buttons_bg_clr};
        color: ${config.theme.colour.cta_buttons_clr};
        opacity: 0.8;
      }
    }
  `
      : ''
  }
`,
    recipeList: [
      EmailPassword.init({
        preAPIHook: customPreAPIHook,
        signInAndUpFeature: (() => {
          if (config.configuration.tenant_id === tenantMap.BSO)
            return {
              signUpForm: {
                formFields: [
                  {
                    id: 'email',
                    label: 'AUTH_SIGNUP_BSO_EMAIL',
                    placeholder: 'johndoe@example.com',
                    optional: false,
                  },
                  {
                    id: 'password',
                    label: 'AUTH_SIGNUP_BSO_PASSWORD',
                    placeholder: '',
                    optional: false,
                    validate: async (
                      value: string,
                    ): Promise<string | undefined> => {
                      const cleaned = value.replace(/\s/g, '');
                      if (cleaned.length < 8) {
                        return 'AUTH_SIGNUP_BSO_PASSWORD_REQUIREMENTS';
                      }
                      const hasLetter = /[a-zA-Z]/.test(cleaned);
                      const hasDigit = /[0-9]/.test(cleaned);
                      if (!hasLetter || !hasDigit) {
                        return 'AUTH_SIGNUP_BSO_PASSWORD_REQUIREMENTS';
                      }
                      return undefined;
                    },
                  },
                  {
                    id: 'customer.salutationKey',
                    label: '',
                    placeholder: '',
                    optional: true,
                    inputComponent: SalutationSelector,
                    getDefaultValue: () => getDefaultSalutation(),
                  },
                  {
                    id: 'customer.forename',
                    label: 'AUTH_SIGNUP_BSO_FIRST_NAME',
                    placeholder: 'John',
                    optional: false,
                    validate: async (value: string) =>
                      validateTextField(
                        value,
                        'AUTH_PAGE_FIRST_NAME_VALID_TEXT',
                      ),
                  },
                  {
                    id: 'customer.surname',
                    label: 'AUTH_SIGNUP_BSO_SURNAME',
                    placeholder: 'Doe',
                    optional: false,
                    validate: async (value: string) =>
                      validateTextField(value, 'AUTH_PAGE_SURNAME_VALID_TEXT'),
                  },
                  {
                    id: 'customer.street',
                    label: 'AUTH_SIGNUP_BSO_STREET',
                    placeholder: 'Hauptstraße',
                    optional: false,
                    validate: async (value: string) =>
                      validateTextField(value, 'AUTH_PAGE_STREET_VALID_TEXT'),
                  },
                  {
                    id: 'customer.houseNumber',
                    label: 'AUTH_SIGNUP_BSO_HOUSE_NO',
                    placeholder: '123',
                    optional: false,
                    validate: async (value: string) =>
                      validateTextField(
                        value,
                        'AUTH_PAGE_HOUSE_NUMBER_VALID_TEXT',
                      ),
                  },
                  {
                    id: 'customer.zipcode',
                    label: 'AUTH_SIGNUP_BSO_POSTCODE',
                    placeholder: '10115',
                    optional: false,
                    validate: async (value: string) =>
                      validateTextField(value, 'AUTH_PAGE_POSTCODE_VALID_TEXT'),
                  },
                  {
                    id: 'customer.city',
                    label: 'AUTH_SIGNUP_BSO_CITY',
                    placeholder: 'Berlin',
                    optional: false,
                    validate: async (value: string) =>
                      validateTextField(value, 'AUTH_PAGE_CITY_VALID_TEXT'),
                  },
                  {
                    id: 'customer.countryKey',
                    label: '',
                    placeholder: '',
                    optional: true,
                    inputComponent: CountrySelector,
                    getDefaultValue: () => '1',
                  },
                  {
                    id: 'newsletterStatus.1',
                    label: '',
                    placeholder: '',
                    inputComponent: TermsAgreement,
                    optional: false,
                  },
                ],
              },
            };
        })(),
        override: {
          functions: originalImplementation => {
            return {
              ...originalImplementation,
              signUp: async function (input) {
                const currentLang =
                  getCookie('NEXT_LOCALE') ||
                  getCookie('language') ||
                  config.default_language.shortcode;
                const response = await originalImplementation.signUp(input);
                if (response.status === 'FIELD_ERROR') {
                  response.formFields = response.formFields.map(field => {
                    return {
                      ...field,
                      error: getTranslation(field.error, currentLang),
                    };
                  });
                }
                return response;
              },
            };
          },
        },
        getRedirectionURL: async context =>
          context.action === 'RESET_PASSWORD'
            ? `/${getCurrentLocale()}/auth/reset-password`
            : undefined,
        onHandleEvent: async context => {
          if (context.action === 'SUCCESS' && context.createdNewSession) {
            await fetchStoreAndIdentify(
              context.isNewRecipeUser,
              'email',
              handlerDeps,
            );
            if (
              superTokensRouterInstance.router &&
              superTokensRouterInstance.pathName
            ) {
              if (superTokensRouterInstance.pathName.includes('/library')) {
                superTokensRouterInstance.router.push(
                  superTokensRouterInstance.pathName,
                );
              } else {
                superTokensRouterInstance.router.refresh();
              }
            }
            return null;
          }
        },
      }),
      ThirdParty.init({
        preAPIHook: async context => {
          if (context.action === 'GET_AUTHORISATION_URL') {
            const currentLocale =
              getCookie('NEXT_LOCALE') ||
              getCookie('language') ||
              config.default_language.shortcode;
            sessionStorage.setItem('auth_redirect_locale', currentLocale);
          }
          return customPreAPIHook(context);
        },
        onHandleEvent: async context => {
          if (
            context.action === 'SUCCESS' &&
            context.createdNewSession &&
            context.isNewRecipeUser
          ) {
            await fetchStoreAndIdentify(
              true,
              context.user.thirdParty?.[0]?.id ?? 'social',
              handlerDeps,
            );
          }
        },
      }),
      Session.init({
        tokenTransferMethod: 'header',
        override: {
          functions: originalImplementation => ({
            ...originalImplementation,
            signOut: async function (input) {
              reset?.();
              localStorage.removeItem('userData');
              window.dispatchEvent(new Event('userDataUpdated'));
              return originalImplementation.signOut(input);
            },
          }),
        },
        onHandleEvent: async event => {
          if (
            event.action === 'UNAUTHORISED' &&
            event.sessionExpiredOrRevoked
          ) {
            await Session.signOut();
            if (superTokensRouterInstance.router) {
              superTokensRouterInstance.router.push('/?show=signin');
            } else {
              window.location.href = '?show=signin';
            }
          }
          if (event.action === 'REFRESH_SESSION') {
            await fetchAndStoreAccountDetails(
              event.userContext.isNewRecipeUser,
              handlerDeps,
            );
          }
        },
      }),
      Multitenancy.init({
        override: {
          functions: originalImplementation => ({
            ...originalImplementation,
            getTenantId: async () => config.configuration.tenant_id,
          }),
        },
      }),
    ],
    getRedirectionURL: async context => {
      if (context.action === 'TO_AUTH') {
        return `/${lang}`;
      }
      if (context.action === 'SUCCESS' && context.newSessionCreated) {
        await fetchAndStoreAccountDetails(context.isNewRecipeUser, handlerDeps);
        return null;
      }
    },
  };
};
